import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Group, Paper, Text, ThemeIcon, Title } from '@mantine/core';
import type { StatusTone } from '../tokens/statusColorMap';
import { statusToneToMantineColor } from '../tokens/statusColorMap';

const clickableStyle: CSSProperties = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
};

function maybeLink(to: string | undefined, testId: string | undefined, children: ReactNode) {
  if (!to) {
    return children;
  }

  return (
    <Link to={to} style={clickableStyle} className="metric-card--clickable" data-testid={testId ? `${testId}-link` : undefined}>
      {children}
    </Link>
  );
}

export interface MetricCardProps {
  title: string;
  value: number | string;
  description?: string;
  tone?: StatusTone;
  icon?: ReactNode;
  testId?: string;
  /** When set, the whole card navigates to this path. */
  to?: string;
}

export function MetricCard({
  title,
  value,
  description,
  tone = 'neutral',
  icon,
  testId,
  to,
}: MetricCardProps) {
  const color = statusToneToMantineColor[tone];

  return maybeLink(
    to,
    testId,
    <Paper p="lg" radius="lg" withBorder shadow="xs" data-testid={testId}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        {icon ? (
          <ThemeIcon size={42} radius="md" variant="light" color={color}>
            {icon}
          </ThemeIcon>
        ) : null}
        <Text size="28px" fw={800} lh={1} ta="right" style={{ marginLeft: 'auto' }}>
          {value}
        </Text>
      </Group>
      <Text mt="md" size="sm" fw={700}>{title}</Text>
      {description ? (
        <Text mt={6} size="sm" c="dimmed">{description}</Text>
      ) : null}
    </Paper>,
  );
}

export function QualityTile({
  title,
  value,
  subtitle,
  tone = 'neutral',
  to,
  testId,
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  tone?: StatusTone;
  to?: string;
  testId?: string;
}) {
  const color = statusToneToMantineColor[tone];

  return maybeLink(
    to,
    testId,
    <Paper
      p="md"
      radius="md"
      withBorder
      bg={`var(--mantine-color-${color}-0)`}
      data-testid={testId}
    >
      <Text size="xs" fw={700} c={`${color}.8`} tt="uppercase">{title}</Text>
      <Title order={3} mt={4}>{value}</Title>
      {subtitle ? <Text size="xs" c="dimmed" mt={4}>{subtitle}</Text> : null}
    </Paper>,
  );
}
